/**
 * AddOnsPanel — the QSR industry add-on / monetisation model. Each add-on is an
 * industry innovation the operator can switch on; billed per active staff member.
 * Toggling drives which optional modules appear (e.g. Waste & Voids tab).
 */
import React from 'react';
import { Switch } from '@/components/ui/switch';
import { Puzzle, TrendingUp, Trash2, Timer, Layers, Sparkles } from 'lucide-react';
import { STAFF_COUNT, money } from './qsrDemoData';

export const QSR_ADDONS = [
  { key: 'forecast', name: 'Day-Part Sales Forecast', icon: TrendingUp, price: 2, desc: 'Predicts sales by day-part so managers roster ahead of the dinner peak.' },
  { key: 'waste', name: 'Waste & Voids Guard', icon: Trash2, price: 3, desc: 'Tracks waste & voids by store and reason, with AI margin alerts.' },
  { key: 'speedcoach', name: 'Speed Coach', icon: Timer, price: 4, desc: 'Live drive-through pacing nudges to crew during peak windows.' },
  { key: 'upsell', name: 'Combo Upsell Prompts', icon: Layers, price: 2, desc: 'POS-scripted upsell prompts to lift combo attachment.' },
];

export default function AddOnsPanel({ addons = {}, onToggle, staffCount = STAFF_COUNT }) {
  const monthly = QSR_ADDONS.reduce((s, a) => s + (addons[a.key] ? a.price * staffCount : 0), 0);

  return (
    <div className="rounded-3xl qsr-card p-5 shadow-sm" data-testid="addons-panel">
      <div className="flex items-center gap-2.5 mb-1">
        <div className="p-2 rounded-xl bg-violet-50 border border-violet-200"><Puzzle className="w-5 h-5 text-violet-600" /></div>
        <div>
          <h3 className="font-semibold text-base tracking-tight text-slate-900">QSR add-ons</h3>
          <p className="text-xs text-slate-500">Switch on industry innovations · billed per active staff member</p>
        </div>
      </div>

      <div className="mt-4 space-y-2.5">
        {QSR_ADDONS.map((a) => {
          const on = !!addons[a.key];
          return (
            <div key={a.key} className={`flex items-start gap-3 rounded-2xl border p-3.5 transition-colors ${on ? 'border-emerald-200 bg-emerald-50/40' : 'border-slate-200 bg-slate-50'}`} data-testid={`addon-${a.key}`}>
              <div className={`p-1.5 rounded-lg ${on ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-200 text-slate-500'}`}><a.icon className="w-4 h-4" /></div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm text-slate-900">{a.name}</span>
                  <span className="text-[11px] font-semibold text-violet-700 bg-violet-50 border border-violet-200 rounded-full px-2 py-0.5">${a.price}/staff/mo</span>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{a.desc}</p>
                {on && <p className="text-[11px] text-emerald-700 font-medium mt-1">+{money(a.price * staffCount)}/mo · {staffCount} staff</p>}
              </div>
              <Switch checked={on} onCheckedChange={() => onToggle?.(a.key)} data-testid={`addon-toggle-${a.key}`} />
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex items-center justify-between rounded-2xl bg-slate-900 text-white px-4 py-3">
        <div className="flex items-center gap-2 text-sm"><Sparkles className="w-4 h-4 text-emerald-300" /> Add-ons subtotal</div>
        <div className="text-right">
          <div className="text-lg font-semibold">{money(monthly)}<span className="text-xs font-normal text-slate-300">/mo</span></div>
          <div className="text-[10px] text-slate-400">across {staffCount} staff · illustrative</div>
        </div>
      </div>
    </div>
  );
}
